// Per-route SEO metadata.
//
// Every route served the same <title>JOURNIM</title> and the same description,
// so all 26 of them competed as one page and none described itself. Kept in a
// table rather than sprinkled through page components: the set of indexable
// routes is a decision about the site, and it reads better in one place.

/** The host that actually serves. The apex only 301s here. */
export const SITE_ORIGIN = "https://www.journim.com";

const DEFAULT_DESCRIPTION =
  "Journim is an AI-powered travel planner that builds personalised, day-by-day itineraries for destinations across India.";

/** Exactly-matched routes. Anything absent is treated as not indexable. */
export const ROUTE_SEO = {
  "/": {
    title: "Journim — AI trip planner for India",
    description: DEFAULT_DESCRIPTION,
  },
  "/about": {
    title: "About Journim",
    description:
      "Why we built Journim, how the trip planner works, and who it is for.",
  },
  "/treks": {
    title: "Treks in India — routes, seasons and difficulty",
    description:
      "Browse treks across the Himalayas, the Western Ghats and beyond, with the best season, duration and difficulty for each.",
  },
  "/book": {
    title: "Book a trip with Journim",
    description:
      "Tell us where you want to go and we will put a planned, bookable trip together for you.",
  },
  "/contact": {
    title: "Contact Journim",
    description: "Questions, feedback or partnership enquiries — get in touch.",
  },
  "/terms": {
    title: "Terms and conditions",
    description: "The terms that apply when you use Journim.",
  },
  "/privacy": {
    title: "Privacy policy",
    description: "What data Journim collects, why, and how it is handled.",
  },
};

/**
 * Prefix-matched routes.
 *
 * `noindex` here is not a formality. A shared itinerary link is unguessable but
 * public and carries someone's real trip — it must never end up in a search
 * result. The planner funnel and the signed-in app are excluded for a duller
 * reason: without JavaScript they are empty, so indexing them would file thin
 * pages under the site's name.
 */
export const ROUTE_PREFIX_SEO = [
  {
    prefix: "/treks/",
    title: "Trek details — Journim",
    description: "Route, season, difficulty and what to expect on this trek.",
  },
  { prefix: "/i/", title: "Shared itinerary — Journim", noindex: true },
  { prefix: "/demo/", title: "Sample itinerary — Journim", noindex: true },
  { prefix: "/choose-", title: "Plan your trip — Journim", noindex: true },
  { prefix: "/itinerary", title: "Plan your trip — Journim", noindex: true },
  { prefix: "/final-itinerary", title: "Your itinerary — Journim", noindex: true },
  { prefix: "/agent", title: "Agent J — Journim", noindex: true },
  { prefix: "/reel-to-real", title: "Reel to Real — Journim", noindex: true },
  { prefix: "/budget-itinerary", title: "Budget trip — Journim", noindex: true },
  { prefix: "/wishlist", title: "Your wishlist — Journim", noindex: true },
  { prefix: "/yourtrips", title: "Your trips — Journim", noindex: true },
  { prefix: "/profile", title: "Your profile — Journim", noindex: true },
  { prefix: "/login", title: "Log in — Journim", noindex: true },
  { prefix: "/signup", title: "Sign up — Journim", noindex: true },
  { prefix: "/forgot-password", title: "Reset your password — Journim", noindex: true },
  { prefix: "/sso-callback", title: "Signing you in — Journim", noindex: true },
];

/** Resolve a pathname to its metadata. Unknown paths are never indexable. */
export function seoForPath(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;

  if (ROUTE_SEO[path]) return { ...ROUTE_SEO[path], noindex: false };

  const prefixed = ROUTE_PREFIX_SEO.find((r) => path.startsWith(r.prefix));
  if (prefixed) {
    return {
      title: prefixed.title,
      description: prefixed.description || DEFAULT_DESCRIPTION,
      noindex: !!prefixed.noindex,
    };
  }

  // An unmatched path renders the app's 404. It returns HTTP 200 because this
  // is a single-page app, so noindex is what stops it being filed as a real
  // page — see the runbook.
  return {
    title: "Page not found — Journim",
    description: DEFAULT_DESCRIPTION,
    noindex: true,
  };
}
